/**
 * Mobile Drawer Web Component
 * 
 * A sliding drawer component that animates from bottom to top
 * 
 * Usage:
 * <mobile-drawer>
 *   <button slot="trigger" data-drawer-trigger>Open</button>
 *   <div slot="content" data-drawer-content>
 *     <!-- drawer content -->
 *   </div>
 * </mobile-drawer>
 * 
 * Attributes:
 * - animation-duration: Duration in milliseconds (default: 600)
 */
class MobileDrawer extends HTMLElement {
  constructor() {
    super();

    // Initialize state only
    this.animationDuration = parseInt(this.getAttribute('animation-duration')) || 600;
    this.isOpen = false;

    // Bind event handlers once in constructor
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleContentClick = this.handleContentClick.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-drawer-trigger]');
    this.content = this.querySelector('[data-drawer-content]');
    this.closeButtons = this.querySelectorAll('[data-drawer-close]');

    if (!this.trigger || !this.content) {
      console.warn('MobileDrawer: Missing required elements (trigger or content)');
      return;
    }

    this.init();
  }

  init() {
    this.setupAccessibility();
    this.setupEventListeners();
  }

  setupAccessibility() {
    // Set ARIA attributes for accessibility
    const triggerId = this.trigger.id || `drawer-trigger-${Math.random().toString(36).substr(2, 9)}`;
    const contentId = this.content.id || `drawer-content-${Math.random().toString(36).substr(2, 9)}`;

    this.trigger.id = triggerId;
    this.content.id = contentId;

    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-controls', contentId);
    
    if (!this.trigger.hasAttribute('aria-label') && !this.trigger.textContent.trim()) {
      this.trigger.setAttribute('aria-label', 'Open drawer');
    }
  }

  setupEventListeners() {
    // Open on trigger click
    this.trigger.addEventListener('click', this.handleTriggerClick);

    // Close buttons
    this.closeButtons.forEach(button => {
      const handleClose = (e) => {
        e.preventDefault();
        this.close();
      };
      button.addEventListener('click', handleClose);
      
      // Store handler for cleanup
      if (!this.closeButtonHandlers) {
        this.closeButtonHandlers = new WeakMap();
      }
      this.closeButtonHandlers.set(button, handleClose);
    });

    // Close on outside click
    this.content.addEventListener('click', this.handleContentClick);

    // Close on escape key
    this.addEventListener('keyup', this.handleEscapeKey);

    // Close drawer on internal link clicks (if specified)
    this.autoCloseLinks = this.querySelectorAll('[data-drawer-auto-close]');
    this.autoCloseLinks.forEach(link => {
      const handleAutoClose = () => {
        this.close();
      };
      link.addEventListener('click', handleAutoClose);
      
      // Store handler for cleanup
      if (!this.autoCloseHandlers) {
        this.autoCloseHandlers = new WeakMap();
      }
      this.autoCloseHandlers.set(link, handleAutoClose);
    });
  }

  handleTriggerClick(e) {
    e.preventDefault();
    const isExpanded = this.trigger.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      this.close();
    } else {
      this.open();
    }
  }

  handleContentClick(e) {
    if (e.target === this.content) {
      this.close();
    }
  }

  handleEscapeKey(e) {
    if (e.code === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  open() {
    this.isOpen = true;
    
    // Update trigger state
    this.trigger.setAttribute('aria-expanded', 'true');
    
    // Clear any inline visibility style and let CSS handle it
    this.content.style.visibility = 'visible';
    
    // Add open class
    this.content.classList.add('is-open');
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    document.body.classList.add('mobile-menu-open');

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('drawer:open', {
      bubbles: true,
      detail: { drawer: this }
    }));
  }

  close() {
    this.isOpen = false;

    // Update trigger state
    this.trigger.setAttribute('aria-expanded', 'false');
    
    // Ensure visibility stays visible during animation
    this.content.style.visibility = 'visible';
    
    // Remove open class to trigger transform animation
    this.content.classList.remove('is-open');
    
    // Restore body scroll
    document.body.style.overflow = '';
    document.body.classList.remove('mobile-menu-open');

    // Delay visibility change to allow animation to complete
    setTimeout(() => {
      if (!this.content.classList.contains('is-open')) {
        this.content.style.visibility = 'hidden';
      }
    }, this.animationDuration);

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('drawer:close', {
      bubbles: true,
      detail: { drawer: this }
    }));
  }

  // Public API methods
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  // Attribute change observer
  static get observedAttributes() {
    return ['animation-duration'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'animation-duration' && oldValue !== newValue) {
      this.animationDuration = parseInt(newValue) || 600;
    }
  }

  disconnectedCallback() {
    // Remove trigger event listener
    if (this.trigger) {
      this.trigger.removeEventListener('click', this.handleTriggerClick);
    }

    // Remove close button event listeners
    if (this.closeButtons && this.closeButtonHandlers) {
      this.closeButtons.forEach(button => {
        const handler = this.closeButtonHandlers.get(button);
        if (handler) {
          button.removeEventListener('click', handler);
        }
      });
    }

    // Remove content click listener
    if (this.content) {
      this.content.removeEventListener('click', this.handleContentClick);
    }

    // Remove escape key listener
    this.removeEventListener('keyup', this.handleEscapeKey);

    // Remove auto-close link listeners
    if (this.autoCloseLinks && this.autoCloseHandlers) {
      this.autoCloseLinks.forEach(link => {
        const handler = this.autoCloseHandlers.get(link);
        if (handler) {
          link.removeEventListener('click', handler);
        }
      });
    }

    // Clean up body classes if drawer is open
    if (this.isOpen) {
      document.body.style.overflow = '';
      document.body.classList.remove('mobile-menu-open');
    }
  }
}

customElements.define('mobile-drawer', MobileDrawer);
